//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse} from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import ActivityTrackerModel from './activityTracker.model';

/**
 * @service ActivityTrackerService
 * @extends Service<ActivityTrackerModel>
 * @description
 * Service for managing activity tracking operations and geolocation data retrieval.
 *
 * This service extends the base `Service` class to provide specific functionality for
 * activity tracking, including reverse geocoding to obtain address details from coordinates.
 *
 * ## Key Responsibilities:
 * 1. **Geolocation**: Fetches address details (reverse geocoding) using MapmyIndia API based on latitude and longitude.
 * 2. **Request Management**: Tracks and cancels ongoing HTTP requests to prevent memory leaks and race conditions.
 *
 * ## API Endpoints:
 * - Base Internal URL: `/aqu/activitytracker/`
 * - External API: `https://apis.mapmyindia.com/advancedmaps/v1/.../rev_geocode`
 */
@Injectable({
  providedIn: 'root'
})
export class ActivityTrackerService extends Service<ActivityTrackerModel> {

  /**
   * @type {Subscription[]}
   * @description
   * Array to store ongoing HTTP request subscriptions.
   * Used to track active requests so they can be cancelled if the component is destroyed or if needed.
   * @default []
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * Constructor for ActivityTrackerService.
   * Initializes the service with HttpClient and sets the base URL for activity tracker API.
   *
   * @param {HttpClient} httpClient - Angular HttpClient for making HTTP requests.
   * @service HttpClient
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/aqu/activitytracker/');
  }

  /**
   * @method getLatLangDetails
   * @async
   * @description
   * Retrieves address details for a specific latitude and longitude using the MapmyIndia Reverse Geocoding API.
   *
   * ## Logic Flow:
   * 1. Constructs a GET request to the MapmyIndia API with the provided API key and coordinates.
   * 2. Subscribes to the observable response.
   * 3. Wraps the operation in a Promise to handle asynchronous resolution or rejection.
   * 4. Pushes the subscription to `ongoingRequests` for lifecycle management.
   *
   * @param {any} lat - The latitude coordinate.
   * @param {any} lng - The longitude coordinate.
   * @returns {Promise<ActivityTrackerModel>} A promise that resolves with the location details (ActivityTrackerModel).
   * @throws {HttpErrorResponse} If the external API call fails.
   * @side-effect Adds a subscription to `ongoingRequests`.
   *
   * @example
   * service.getLatLangDetails(17.4400848, 78.4202012).then(details => {
   *   console.log(details);
   * });
   */
  public getLatLangDetails(lat, lng) {
    return new Promise<ActivityTrackerModel>((resolve, reject) => {
      const request = this.httpClient.get<ActivityTrackerModel>(`https://apis.mapmyindia.com/advancedmaps/v1/239594381b6a2b40efde1e8817222b24/rev_geocode?lat=${lat}&lng=${lng}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method cancelOnGoingHttpRequests
   * @description
   * Cancels all currently active HTTP requests initiated by this service.
   *
   * ## Logic Flow:
   * 1. Iterates through the `ongoingRequests` array.
   * 2. Checks if each item is a valid subscription with an `unsubscribe` method.
   * 3. Calls `unsubscribe()` to cancel the request and free resources.
   * 4. Resets the `ongoingRequests` array to an empty state.
   *
   * Use this method in the `ngOnDestroy` lifecycle hook of components consuming this service.
   *
   * @returns {void}
   * @side-effect Unsubscribes from Observables and clears `ongoingRequests`.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
